import { useState, type FormEvent } from 'react';

const FORM_ID = 'subscribe-form-bola';
const SUBMIT_URL = 'https://readdy.ai/api/form/dain2ujoh653ivfvogqg';
const MAX_LEAD = 500;

export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [leadLength, setLeadLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setLeadLength(0);
      return;
    }

    setStatus('submitting');
    setFormError('');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        params.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setFormError('');
        form.reset();
        setLeadLength(0);
      } else {
        setStatus('error');
        setFormError(
          serverMsg && serverMsg.trim()
            ? String(serverMsg)
            : '提交未成功，请稍后重试，或直接发送邮件到 tips@18bolaodaily.example。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，没能提交成功，请检查网络后重试。');
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="mb-5">
        <h3 className="font-heading text-xl font-bold text-foreground-950">订阅爆料 · 每日追更</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
          留下邮箱，每天下午把当天最值得看的三条爆料发给你。也可以顺手投一条线索。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">
            邮箱 <span className="text-primary-500">*</span>
          </span>
          <input
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">最想追的栏目</span>
          <select
            name="topic"
            defaultValue="star"
            className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="star">明星八卦</option>
            <option value="influencer">网红翻车</option>
            <option value="submit">素人投稿</option>
            <option value="deep">深度追踪</option>
          </select>
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-800">线索描述（选填）</span>
        <textarea
          name="lead"
          rows={4}
          maxLength={MAX_LEAD}
          onChange={(e) => setLeadLength(e.target.value.length)}
          placeholder="说说你看到的爆料，越具体越好：时间、地点、你在现场看到的细节……"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
        <span className="self-end text-xs text-foreground-400">
          {leadLength} / {MAX_LEAD}
        </span>
      </label>

      <input
        type="text"
        name="website_alt"
        className="alt-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
            正在提交
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" aria-hidden="true"></i>
            订阅并提交
          </>
        )}
      </button>

      {status === 'success' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
          已收到，感谢！我们会按你选择的栏目发送每日精选。
        </p>
      )}
      {status === 'error' && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
          <span>{formError}</span>
        </p>
      )}
    </form>
  );
}